/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import { Survey, ReactSurveyElement, ReactElementFactory } from 'survey-react-ui';
import { Model, Serializer } from 'survey-core';
import { transformDataToSurveyJSON } from '../utils/surveyTransformer';
import { applyWafEncoding, DEFAULT_WAF_TOKEN_MAP } from '../utils/wafEncoder';
import { parseCheckboxAnswer } from '../utils/answerParser';
import { HelpModal } from "../components/HelpModal";
import Avatar from './Avatar';
import { Toast, ToastContainer } from 'react-bootstrap';
import { trackEvent } from '../lib/appInsights';


Serializer.addProperty("survey", "progressTitle");
class PercentageProgressBar extends ReactSurveyElement {
    render() {
        if (this.props.model.state === 'preview') {
            return null;
        }
        const progressValue = this.props.model.progressValue || 0;
        return (
            <div className="sv-progressbar-percentage-container">
                <div className="sv-progressbar-percentage__text">
                    <span className="sv-progressbar-percentage__title">{this.props.model.progressTitle}</span>
                    <span className="sv-progressbar-percentage__value">{progressValue + "%"}</span>
                </div>
                <div className="sv-progressbar-percentage__indicator">
                    {progressValue > 0 && (
                        <div
                            className="sv-progressbar-percentage__value-bar"
                            style={{ width: progressValue + "%" }}
                        ></div>
                    )}
                </div>
            </div>
        );
    }
}
ReactElementFactory.Instance.registerElement("sv-progressbar-percentage", props => {
    return React.createElement(PercentageProgressBar, props);
});

const MemoizedSurvey = memo(Survey);

function QuestionnaireView({ jwt, questionnaireId, onBack, onIdChange, isReadOnly, isDynamicsMode, isPortalMode, wafTokenMap }) {
    const [surveyModel, setSurveyModel] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saveStatus, setSaveStatus] = useState('Saved');
    const [internalQuestionnaireId] = useState(questionnaireId);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState(null);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [canSubmit] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [questionnaireVersion, setQuestionnaireVersion] = useState(null);
    const [connectedUsers, setConnectedUsers] = useState([]);
    const [typingUsers, setTypingUsers] = useState(new Set());
    const [toasts, setToasts] = useState([]);

    const isSavingRef = useRef(false);
    const isRemoteUpdateRef = useRef(false);
    const clientUpdateDebounceRef = useRef(null);
    const socketRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const jwtRef = useRef(jwt);
    const previousUsersRef = useRef([]);
    const initialLoadRef = useRef(true);
    const surveyModelRef = useRef(null);
    const viewerPhotoRef = useRef(null);

    useEffect(() => {
        surveyModelRef.current = surveyModel;
    }, [surveyModel]);

    // ref to hold the dynamic link map
    const dynamicPanelMapRef = useRef(new Map());

    useEffect(() => {
        jwtRef.current = jwt;
    }, [jwt]);


    const handleSave = useCallback((surveyData) => {
        return new Promise((resolve, reject) => {
            void (async () => {
                if (isSavingRef.current || isReadOnly) {
                    resolve();
                    return;
                }
                isSavingRef.current = true;
                setSaveStatus('Saving...');
                try {
                    const api = axios.create({ headers: { Authorization: `Bearer ${jwtRef.current}` } });
                    await api.post(`/api/saveProgress/${internalQuestionnaireId}`, { answers: applyWafEncoding(surveyData, wafTokenMap ?? DEFAULT_WAF_TOKEN_MAP) });
                    setSaveStatus('Saved');
                    resolve();
                } catch (err) {
                    console.error("Failed to save progress:", err);
                    setSaveStatus('Error');
                    reject(err);
                } finally {
                    isSavingRef.current = false;
                }
            })();
        });
    }, [internalQuestionnaireId, isReadOnly]);


    useEffect(() => {
        const loadQuestionnaire = async () => {
            setIsLoading(true);
            setError(null);
            setSurveyModel(null);
            const api = axios.create({ headers: { Authorization: `Bearer ${jwtRef.current}` } });

            try {
                const response = await api.get(`/api/getQuestionnaireDetails/${questionnaireId}`);
                const data = response.data;
                const schemaData = data.Questions || data.questions;
                const answersData = data.Answers || data.answers;

                // Store version info for display
                const versionInfo = data.Version || data.version;
                if (versionInfo) {
                    setQuestionnaireVersion(versionInfo);
                }

                const surveyJson = transformDataToSurveyJSON({ Questions: schemaData });

                const links = surveyJson.dynamicPanelLinks || [];
                dynamicPanelMapRef.current = new Map(links.map(link => [link.source, link.target]));

                const survey = new Model(surveyJson);
                survey.clearInvisibleValues = "onHidden";
                survey.showPreviewBeforeComplete = "showAllQuestions";
                survey.showCompletedPage = false;
                survey.progressTitle = "Questionnaire Progress";
                survey.textUpdateMode = "onTyping";

                if (isReadOnly) {
                    survey.mode = 'display';
                }

                const questionTypeMap = new Map();
                surveyJson.pages?.forEach(page => {
                    page.elements?.forEach(question => {
                        questionTypeMap.set(question.name, question.type);
                    });
                });

                if (answersData && Array.isArray(answersData)) {
                    const surveyData = answersData.reduce((dataObject, currentAnswer) => {
                        const valueToUse = currentAnswer.clientAnswers ?? currentAnswer.answerText;
                        if (currentAnswer.questionId && valueToUse !== null) {
                            const questionType = questionTypeMap.get(currentAnswer.questionId);
                            if (questionType === 'paneldynamic' && typeof valueToUse === 'string') {
                                try {
                                    dataObject[currentAnswer.questionId] = JSON.parse(valueToUse);
                                } catch (e) {
                                    dataObject[currentAnswer.questionId] = [];
                                }
                            } else if (questionType === 'checkbox' && typeof valueToUse === 'string') {
                                // Multi-select answers are stored as a JSON array so option values
                                // containing commas survive the round-trip (with a legacy comma-split
                                // fallback for answers saved before this format change).
                                dataObject[currentAnswer.questionId] = parseCheckboxAnswer(valueToUse);
                            } else {
                                dataObject[currentAnswer.questionId] = valueToUse;
                            }
                        }
                        return dataObject;
                    }, {});
                    survey.data = surveyData;
                }

                if (!survey.isDisplayMode && !survey.isPreviewMode) {
                    survey.addLayoutElement({
                        id: "progressbar-percentage",
                        component: "sv-progressbar-percentage",
                        container: "contentTop",
                        data: survey
                    });
                }

                setSurveyModel(survey);

            } catch (err) {
                setError(err.response?.data?.message || 'Failed to load questionnaire.');
            } finally {
                setIsLoading(false);
            }
        };

        if (questionnaireId) {
            void loadQuestionnaire();
        }

        return () => {
            // No cleanup needed for autoSaveTimeoutRef as it's removed
        };
    }, [questionnaireId, isReadOnly]);


    // Socket.IO Integration
    useEffect(() => {
        if (!jwt || !questionnaireId) return;

        socketRef.current = io({
            auth: {
                token: jwt
            }
        });

        socketRef.current.emit('join-room', { questionnaireId, jwt, photo: viewerPhotoRef.current });

        socketRef.current.on('room-users-list', ({ users }) => {
            // Ensure unique users by email
            const uniqueUsers = Array.from(new Map(users.map(u => [u.email, u])).values());

            if (!initialLoadRef.current) {
                const prevEmails = new Set(previousUsersRef.current.map(u => u.email));
                const currentEmails = new Set(uniqueUsers.map(u => u.email));

                const joined = uniqueUsers.filter(u => !prevEmails.has(u.email));
                const left = previousUsersRef.current.filter(u => !currentEmails.has(u.email));

                joined.forEach(u => {
                    const id = crypto.randomUUID();
                    setToasts(prev => [...prev, { id, message: `${u.email} joined the session`, type: 'connect' }]);
                });
                left.forEach(u => {
                    const id = crypto.randomUUID();
                    setToasts(prev => [...prev, { id, message: `${u.email} left the session`, type: 'disconnect' }]);
                });
            }

            initialLoadRef.current = false;
            previousUsersRef.current = uniqueUsers;
            setConnectedUsers(uniqueUsers);
        });

        socketRef.current.on('user-typing', ({ email, isTyping }) => {
            setTypingUsers(prev => {
                const newSet = new Set(prev);
                if (isTyping) {
                    newSet.add(email);
                } else {
                    newSet.delete(email);
                }
                return newSet;
            });
        });

        socketRef.current.on('update-answers', ({ answers }) => {
            if (surveyModelRef.current) {
                isRemoteUpdateRef.current = true;
                surveyModelRef.current.data = answers;
                isRemoteUpdateRef.current = false;
            }
        });

        socketRef.current.on('questionnaire-submitted', () => {
            setIsSubmitting(true);
            setSubmitMessage('The questionnaire is being submitted by another user. Please wait...');
        });

        socketRef.current.on('questionnaire-submitted-success', () => {
            setSubmitMessage('Questionnaire submitted successfully! Redirecting...');
            setTimeout(() => onBack(), 3000);
        });

        socketRef.current.on('questionnaire-submission-failed', ({ error }) => {
            setIsSubmitting(false);
            setSubmitMessage(null);
            setError(error || 'Submission failed.');
        });

        socketRef.current.on('save-status', ({ status }) => {
            if (status === 'Error') {
                console.error("Server reported save error");
            }
            setSaveStatus(status);
        });

        return () => {
            if (socketRef.current) socketRef.current.disconnect();
        };
    }, [jwt, questionnaireId]);


    // When embedded in Dynamics, fetch the logged-in viewer's Entra/M365 profile picture
    // from the backend (identity is derived server-side from the JWT) and broadcast it to
    // the room so everyone sees the avatar.
    useEffect(() => {
        if (!isDynamicsMode || !jwt) return;

        let cancelled = false;

        const fetchViewerPhoto = async () => {
            try {
                const response = await axios.get('/api/getViewerPhoto', {
                    headers: { Authorization: `Bearer ${jwt}` }
                });
                const photo = response.data?.photo;
                if (cancelled || typeof photo !== 'string' || !photo) return;

                viewerPhotoRef.current = photo;
                if (socketRef.current) {
                    socketRef.current.emit('set-avatar', { questionnaireId, photo });
                }
            } catch (e) {
                // Photo is non-essential - presence works without it.
            }
        };

        void fetchViewerPhoto();

        return () => {
            cancelled = true;
        };
    }, [isDynamicsMode, questionnaireId, jwt]);


    useEffect(() => {
        if (!surveyModel || isReadOnly) return;

        const valueChangedCallback = (sender, options) => {
            if (isRemoteUpdateRef.current) return;

            const targetPanelName = dynamicPanelMapRef.current.get(options.name);
            if (targetPanelName) {
                const panelQuestion = sender.getQuestionByName(targetPanelName);
                if (panelQuestion) {
                    const newCount = Number.parseInt(options.value, 10) || 0;
                    if (panelQuestion.panelCount !== newCount) {
                        panelQuestion.panelCount = newCount;
                    }
                }
            }


            setSaveStatus('Unsaved');

            // Emit typing event
            if (socketRef.current) {
                socketRef.current.emit('client-typing', { questionnaireId: internalQuestionnaireId, isTyping: true });

                if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = setTimeout(() => {
                    if (socketRef.current) {
                        socketRef.current.emit('client-typing', { questionnaireId: internalQuestionnaireId, isTyping: false });
                    }
                }, 1000);
            }


            if (clientUpdateDebounceRef.current) {
                clearTimeout(clientUpdateDebounceRef.current);
            }

            clientUpdateDebounceRef.current = setTimeout(() => {
                if (socketRef.current) {
                    socketRef.current.emit('client-update', {
                        questionnaireId: internalQuestionnaireId,
                        answers: sender.data,
                        jwt: jwtRef.current
                    });
                }
            }, 300);
        };

        surveyModel.onValueChanged.add(valueChangedCallback);
        return () => surveyModel.onValueChanged.remove(valueChangedCallback);
    }, [surveyModel, handleSave, isReadOnly, internalQuestionnaireId]);



    useEffect(() => {
        if (!surveyModel) return;

        const handlePreviewStart = () => setIsPreviewMode(true);
        const handlePageChange = (sender) => {
            if (sender.state !== "preview") {
                setIsPreviewMode(false);
            }
        };


        surveyModel.onShowingPreview.add(handlePreviewStart);
        surveyModel.onCurrentPageChanged.add(handlePageChange);

        setIsPreviewMode(surveyModel.state === "preview");

        return () => {
            surveyModel.onShowingPreview.remove(handlePreviewStart);
            surveyModel.onCurrentPageChanged.remove(handlePageChange);
        };
    }, [surveyModel]);




    const handleStickySubmitClick = useCallback(() => {
        if (!surveyModel) return;

        // Validate current page before continuing
        const isPageValid = surveyModel.currentPage?.hasErrors(false, true) === false;

        if (!isPageValid) {
            // This will show validation errors if any required fields are missing
            surveyModel.validateCurrentPage();
            return;
        }

        if (surveyModel.state === "running") {
            surveyModel.showPreview();
        } else if (surveyModel.state === "preview") {
            surveyModel.completeLastPage();
        }
    }, [surveyModel]);



    const handleSurveyComplete = useCallback(async (sender) => {
        setIsSubmitting(true);
        setSubmitMessage('Submitting, please wait...');
        setError(null);
        try {
            await handleSave(sender.data);
            const api = axios.create({ headers: { Authorization: `Bearer ${jwtRef.current}` } });
            await api.post('/api/submitQuestionnaire', { submissionid: questionnaireId });
            trackEvent('QuestionnaireSubmitted');
            setSubmitMessage('Questionnaire submitted successfully! Redirecting...');
            setTimeout(() => onBack(), 3000);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'An error occurred during submission.';
            setError(errorMessage);
            setSubmitMessage(null);
            setIsSubmitting(false);
        }
    }, [handleSave, questionnaireId, onBack]);

    const handleBack = async () => {
        if (saveStatus === 'Unsaved' && !isReadOnly && surveyModel) {
            try {
                await handleSave(surveyModel.data);
            } catch (err) {
                console.error("Auto-save on back failed:", err);
            }
        }
        onBack();
    };

    useEffect(() => {
        if (!surveyModel) return;
        surveyModel.onComplete.add(handleSurveyComplete);
        return () => {
            surveyModel.onComplete.remove(handleSurveyComplete);
        };
    }, [surveyModel, handleSurveyComplete]);

    if (isLoading) {
        return <div className="survey-loading-spinner"><div className="spinner"></div></div>;
    }

    if (isSubmitting || (submitMessage && !error)) {
        return (
            <div className="survey-submit-message">
                <div className="survey-submit-card">
                    {!submitMessage && <div className="spinner"></div>}
                    {submitMessage && <svg className="checkmark-icon" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>}
                    <p>{submitMessage || 'Submitting...'}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return <div className="survey-error-alert">{error}</div>;
    }

    if (!surveyModel) {
        return <div className="survey-error-alert">Could not initialize the survey model.</div>;
    }

    return (
        <div className="survey-container">
            <header
                className="survey-header"
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1000
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {!isDynamicsMode && (
                        <button onClick={handleBack} className="survey-back-button">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 19-7-7 7-7" /><path d="M19 12H5" /></svg>
                            Back to Questionnaires
                        </button>
                    )}
                    {questionnaireVersion && (
                        <span className="questionnaire-version-badge">
                            v{questionnaireVersion}
                        </span>
                    )}
                </div>
                {internalQuestionnaireId !== 'new' && !isReadOnly && (
                    <div className="survey-header-actions">
                        <div className="avatar-list">
                            {connectedUsers.map((user) => (
                                <Avatar
                                    key={user.email}
                                    email={user.email}
                                    isTyping={typingUsers.has(user.email)}
                                    photo={user.photo}
                                />
                            ))}
                        </div>
                        <span className="survey-save-status">
                            {saveStatus === 'Saving...' && 'Saving...'}
                            {saveStatus === 'Saved' && 'All changes saved'}
                            {saveStatus === 'Unsaved' && 'Unsaved changes'}
                            {saveStatus === 'Error' && 'Save failed'}
                        </span>
                        <button
                            className="survey-save-button"
                            onClick={() => handleSave(surveyModel.data)}
                            disabled={saveStatus === 'Saving...' || saveStatus === 'Saved'}
                        >
                            {saveStatus === 'Saving...' ? '...' : 'Save'}
                        </button>
                        <button
                            className="survey-help-button"
                            onClick={() => setShowHelpModal(true)}
                        >
                            Help
                        </button>

                        <button
                            className="survey-submit-button"
                            onClick={handleStickySubmitClick}
                            title={!canSubmit ? "Please complete all required questions" : ""}
                        >
                            {isPreviewMode ? "Complete" : "Submit"}
                        </button>
                    </div>
                )}
            </header>

            <main className="survey-main-content">
                <MemoizedSurvey model={surveyModel} />
            </main>

            <HelpModal
                show={showHelpModal}
                onClose={() => setShowHelpModal(false)}
                jwtRef={jwtRef}
                internalQuestionnaireId={internalQuestionnaireId}
            />

            <ToastContainer position="top-end" className="p-3 questionnaire-toast-container" style={{ position: 'fixed', zIndex: 9999 }}>
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        className="questionnaire-toast"
                        onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        show={true}
                        delay={3000}
                        autohide
                    >
                        <Toast.Header closeButton={true}>
                            {toast.type === 'connect' ? (
                                <svg className="toast-connect-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></svg>
                            ) : (
                                <svg className="toast-disconnect-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>
                            )}
                            <strong className="me-auto">Session Update</strong>
                        </Toast.Header>
                        <Toast.Body>{toast.message}</Toast.Body>
                    </Toast>
                ))}
            </ToastContainer>
        </div>
    );

}

export default QuestionnaireView;
